import React from 'react'
import { useNavigate } from '@tanstack/react-router'
import type { InferResponseType } from 'hono/client'
import { CircleCheckIcon } from 'lucide-react'
import { toast } from 'sonner'

import { useModalState } from '@/hooks/use-modal-state'
import { confirm } from '@/components/confirmation'
import { apiClient } from '@/apiClient'
import { useDeleteDocuments } from '@/features/documents/hooks/use-delete-documents'
import { downloadFile } from '@/features/files/hooks/use-download-file'

// Type for the single document response from the API
type DocumentWithDetails = InferResponseType<
  (typeof apiClient.documents)[':documentId']['$get']
>['data']

export function useDocumentActions(document: DocumentWithDetails | undefined) {
  const changeStatusModalCtrl = useModalState()
  const navigate = useNavigate({ from: '/documents/$documentId' })

  const deleteDocumentsMutation = useDeleteDocuments({
    onSuccess: () => {
      toast('Documento eliminato', {
        icon: React.createElement(CircleCheckIcon),
      })
      navigate({ to: '/documents/browse' })
    },
  })

  const handleChangeStatusBtnClick = () => {
    changeStatusModalCtrl.open()
  }

  const handleDeleteBtnClick = () => {
    confirm({
      title: 'Elimina documento',
      content: 'Sei sicuro di voler eliminare il documento?',
      okButtonVariant: 'destructive',
      okText: 'Elimina',
      async onOk() {
        if (document?.id) {
          await deleteDocumentsMutation.mutateAsync({
            documentIds: [document.id],
          })
        }
      },
    })
  }

  const handleDownloadBtnClick = async () => {
    if (!document?.file_id) {
      toast('Impossibile scaricare il documento', {
        description: 'File non trovato',
        icon: React.createElement(CircleCheckIcon),
      })
      return
    }

    try {
      await downloadFile({
        fileId: document.file_id,
        downloadName: document.name || 'document',
        contentDisposition: 'attachment',
      })
      toast('Download avviato', { icon: React.createElement(CircleCheckIcon) })
    } catch (error) {
      toast('Errore durante il download', {
        description:
          'Si è verificato un errore durante il download del documento',
        icon: React.createElement(CircleCheckIcon),
      })
    }
  }

  return {
    changeStatusModalCtrl,
    handleChangeStatusBtnClick,
    handleDeleteBtnClick,
    handleDownloadBtnClick,
  }
}
